import { Injectable } from '@angular/core';
import { Pet } from '@app/models/pet.model';
import { Site } from '@app/models/site.model';
import { map, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CUSTOMER_ID, LocalStorageService, ONLINE_ID, PET_LIST, SITE } from './local-storage.service';
import { PetService } from './pet.service';

const ROUTE = {
    REVIEW: 'review',
    PET_LIST: 'pet-list',
    PET_INFORMATION: 'pet-information',
} as const;

@Injectable({
    providedIn: 'root',
})
export class PetRoutingService {
    constructor(
        private localStorageService: LocalStorageService,
        private petService: PetService
    ) {}

    public getNextRouteAfterClient(): Observable<string> {
        const site = this.localStorageService.get(SITE) as Site;

        if (!site?.supportsPets) {
            return of(ROUTE.REVIEW);
        }

        const customerId = this.localStorageService.getString(CUSTOMER_ID);
        const onlineId = this.localStorageService.getString(ONLINE_ID);

        if (!customerId || !onlineId) {
            return of(ROUTE.PET_INFORMATION);
        }

        return this.checkPetListAndDetermineRoute(customerId, onlineId);
    }

    private checkPetListAndDetermineRoute(customerId: string, onlineId: string): Observable<string> {
        if (this.localStorageService.has(PET_LIST)) {
            const petList = this.localStorageService.get(PET_LIST) as Pet[];
            return of(this.getRouteBasedOnPetList(petList));
        }

        return this.petService.getPetList(customerId, onlineId).pipe(
            map((pets: Pet[]) => this.getRouteBasedOnPetList(pets)),
            catchError(() => of(ROUTE.PET_INFORMATION))
        );
    }

    private getRouteBasedOnPetList(pets: Pet[] | null): string {
        return pets && pets.length > 0 ? ROUTE.PET_LIST : ROUTE.PET_INFORMATION;
    }

    public shouldShowPetRoutes(): boolean {
        const site = this.localStorageService.get(SITE) as Site;
        return site?.supportsPets === true;
    }
}
